import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pencil, Trash2, Moon } from 'lucide-react';

const QUALITY_STYLE = {
  good: 'bg-emerald-50 border-emerald-300 text-emerald-800',
  disturbed: 'bg-amber-50 border-amber-300 text-amber-800',
  poor: 'bg-rose-50 border-rose-300 text-rose-800',
};

function formatDateTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return '—'; }
}

export function SleepChartEntriesTable({ entries, canEdit, onEdit, onArchive }) {
  if (!entries || entries.length === 0) {
    return (
      <div className="py-10 text-center space-y-1" data-testid="sleep-entries-empty">
        <Moon className="w-10 h-10 mx-auto text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">No sleep entries recorded yet.</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="sleep-entries-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b">
            <th className="text-left px-3 py-2">Sleep start</th>
            <th className="text-left px-3 py-2">Sleep end</th>
            <th className="text-left px-3 py-2">Total</th>
            <th className="text-left px-3 py-2">Quality</th>
            <th className="text-left px-3 py-2">Awakenings</th>
            <th className="text-left px-3 py-2">Notes</th>
            <th className="text-right px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id} className="border-b hover:bg-muted/30" data-testid={`sleep-entry-row-${e.id}`}>
              <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(e.sleep_start)}</td>
              <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(e.sleep_end)}</td>
              <td className="px-3 py-2 whitespace-nowrap font-medium">
                {e.total_hours != null ? `${Number(e.total_hours).toFixed(2)} h` : '—'}
              </td>
              <td className="px-3 py-2">
                {e.sleep_quality ? (
                  <Badge variant="outline" className={`text-[10px] capitalize ${QUALITY_STYLE[e.sleep_quality] || ''}`}>
                    {e.sleep_quality}
                  </Badge>
                ) : <span className="text-muted-foreground">—</span>}
              </td>
              <td className="px-3 py-2">{e.night_awakenings != null ? e.night_awakenings : '—'}</td>
              <td className="px-3 py-2 text-xs text-muted-foreground max-w-[260px] truncate" title={e.notes || ''}>
                {e.notes || <span className="italic">(no notes)</span>}
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {canEdit && e.status === 'active' && (
                  <>
                    <Button size="sm" variant="outline" className="h-7 text-xs mr-1" onClick={() => onEdit(e)} data-testid={`sleep-entry-edit-${e.id}`}>
                      <Pencil className="w-3 h-3 mr-1" />Edit
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onArchive(e)} data-testid={`sleep-entry-archive-${e.id}`}>
                      <Trash2 className="w-3 h-3 mr-1" />Archive
                    </Button>
                  </>
                )}
                {e.status === 'archived' && (
                  <Badge variant="outline" className="text-[10px] bg-slate-100 border-slate-300 text-slate-600">Archived</Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default SleepChartEntriesTable;
